/**
 * FilterManager - Handles filtering logic and coordination
 * Manages active filters and updates all managers accordingly
 */
class FilterManager {
    static CONFIG = {
        DEBOUNCE_DELAY: 100, // ms delay for filter updates
        ALL_BUTTON_TYPOLOGY_ID: 'all-typology', 
        ALL_BUTTON_STATUS_ID: 'all-status',
        CLEAR_FILTERS_BUTTON_ID: 'clear-filters',
        VISIBLE_COUNTER_ID: 'filter-count',
    };
    /**
     * Create FilterManager instance
     * @param {Object} dataManager - DataManager instance
     * @param {Object} markerManager - MarkerManager instance
     * @param {Object} clusterHeatManager - ClusterHeatManager instance
     */
    constructor(dataManager, markerManager, clusterHeatManager) {
        if (!dataManager || !markerManager || !clusterHeatManager) {
            throw new Error('FilterManager requires dataManager, markerManager, and clusterHeatManager');
        }
        this.dataManager = dataManager;
        this.markerManager = markerManager;
        this.clusterHeatManager = clusterHeatManager;
        this.activeTypologyIds = new Set();        // Active filters
        this.activeStatusIds = new Set();        // Active filters
        this.debounceTimer = null;        // Debounce timer
        this.onFilterChangeCallbacks = [];        // Filter change callbacks
    }
// ! ---------------------------------------------------------------------------------------------------------------------
    /**
     * Set up DOM event listeners for filter buttons using element IDs
     * Generic method that works for both typology and status filters
     * @param {string} filterType - 'typology' or 'status'
     */
    setupFilterButtons(filterType = 'typology') {
        const isTypology = filterType === 'typology';
        const slugMap = isTypology 
            ? this.dataManager.typologySlugToIdMap 
            : this.dataManager.statusSlugToIdMap;
        const slugs = Array.from(slugMap.keys());
        let attachedCount = 0;
        slugs.forEach(slug => {
            const element = document.getElementById(slug);
            if (element) {
                element.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this._deactivateAllButton(filterType); // Deactivate corresponding "all" button
                    if (isTypology) {  // Toggle the filter
                        this.toggleTypologyBySlug(slug);
                    } else {
                        this.toggleStatusBySlug(slug);
                    }
                    element.classList.toggle('is--active'); // Toggle active class for visual feedback
                    this._checkAndActivateAllButton(filterType); // Check if we need to reactivate "all"
                });
                attachedCount++;
            }
        });
    }
    // ! Set up typology filter buttons
    setupTypologyFilters() {
        this.setupFilterButtons('typology');
    }
    // ! Set up status filter buttons
    setupStatusFilters() {
        this.setupFilterButtons('status');
    }
// ! ---------------------------------------------------------------------------------------------------------------------
    /**
     * ! Set up GENERIC "All" button for both filter types
     * Supports separate "all" buttons for typology and status
     * @param {string} filterType - 'typology' or 'status'
     */
    setupAllButton(filterType = 'typology') { // Look for button with specific ID pattern
        const buttonId = filterType === 'typology' 
            ? FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID 
            : FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
        const allButton = document.getElementById(buttonId);
        if (allButton) {
            allButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (filterType === 'typology') { // Clear only filters of this type
                    this.clearTypologyFilters();
                } else {
                    this.clearStatusFilters();
                }
                allButton.classList.add('is--active'); // Activate this "all" button
            });
            allButton.classList.add('is--active'); // Set as active by default on initialization
        }
    }
// ! ALL-TYPOLOGIES BUTTON - [SETUP]
    setupTypologyAllButton() {
        this.setupAllButton('typology');
    }
// ! ALL-STATUS BUTTON - [SETUP]
    setupStatusAllButton() {
        this.setupAllButton('status');
    }
 // ! CLEAR ALL FILTER BUTTON - [SETUP]
    setupClearAllFiltersButton() {
        const clearButton = document.getElementById(FilterManager.CONFIG.CLEAR_FILTERS_BUTTON_ID);
        if (clearButton) {
            clearButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.clearAllFilters();
            });
        }
    }
// ! VISIBLE COUNTER - [SETUP]
    /**
     * Set up visible item counter
     * Displays and updates the count of visible/filtered items
     */
    setupVisibleCounter() {
        const counterId = FilterManager.CONFIG.VISIBLE_COUNTER_ID;
        // Set initial count immediately
        const counterElement = document.getElementById(counterId);
        if (counterElement) {
            counterElement.textContent = this.dataManager.allLocations.length;
        } else {
            return;
        }
        // Listen for filter changes to update counter automatically
        this.onFilterChange((filteredLocations) => {
            const counterElement = document.getElementById(counterId);
            if (counterElement) {
                counterElement.textContent = filteredLocations.length;
            }
        });
    }
// ! ---------------------------------------------------------------------------------------------------------------------
    // ! TYPOLOGY TOGGLE BY [SLUG]
    /**
     * Toggle typology filter by slug
     * @param {string} slug - Typology slug from DOM
     */
    toggleTypologyBySlug(slug) {
        // Convert slug to ID
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (!typologyId) return;
        this.toggleTypology(typologyId);
    }
    // ! TYPOLOGY TOGGLE BY [ID]
    /**
     * Toggle typology filter by ID
     * @param {string} typologyId - Typology ID
     */
    toggleTypology(typologyId) {
        if (this.activeTypologyIds.has(typologyId)) {
            this.activeTypologyIds.delete(typologyId);
        } else {
            this.activeTypologyIds.add(typologyId);
        }
        this._applyFiltersDebounced();
    }
    // ! TYPOLOGY ADD -- [SLUG]
    /**
     * Add typology filter by slug
     * @param {string} slug - Typology slug
     */
    addTypologyBySlug(slug) {
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (typologyId) {
            this.activeTypologyIds.add(typologyId);
            this._applyFiltersDebounced();
        }
    }
    // ! TYPOLOGY REMOVE -- [SLUG]
    /**
     * Remove typology filter by slug
     * @param {string} slug - Typology slug
     */
    removeTypologyBySlug(slug) {
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (typologyId) {
            this.activeTypologyIds.delete(typologyId);
            this._applyFiltersDebounced();
        }
    }
    // ! -----------------------------------------------------------------------------------------------------------------------
    /**
     * Toggle status filter by slug
     * @param {string} slug - Status slug from DOM
     */
    toggleStatusBySlug(slug) {
        // Convert slug to ID
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (!statusId) return;
        this.toggleStatus(statusId);
    }
    /**
     * Toggle status filter by ID
     * @param {string} statusId - Status ID
     */
    toggleStatus(statusId) {
        if (this.activeStatusIds.has(statusId)) {
            this.activeStatusIds.delete(statusId);
        } else {
            this.activeStatusIds.add(statusId);
        }
        this._applyFiltersDebounced();
    }
    /**
     * Add status filter by slug
     * @param {string} slug - Status slug
     */
    addStatusBySlug(slug) {
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (statusId) {
            this.activeStatusIds.add(statusId);
            this._applyFiltersDebounced();
        }
    }
    /**
     * Remove status filter by slug
     * @param {string} slug - Status slug
     */
    removeStatusBySlug(slug) {
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (statusId) {
            this.activeStatusIds.delete(statusId);
            this._applyFiltersDebounced();
        }
    }
    /**
     * Clear all filters (both typology and status)
     */
    clearAllFilters() {
        this.clearTypologyFilters();
        this.clearStatusFilters(); // Reactivate both "all" buttons
        const typologyAllButton = document.getElementById(FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID);
        if (typologyAllButton) {
            typologyAllButton.classList.add('is--active');
        }
        const statusAllButton = document.getElementById(FilterManager.CONFIG.ALL_BUTTON_STATUS_ID);
        if (statusAllButton) {
            statusAllButton.classList.add('is--active');
        }
        this._applyFiltersNow();
    }
    /**
     * Clear only typology filters
     */
    clearTypologyFilters() {
        this.activeTypologyIds.clear(); // Remove active classes from typology filter buttons
        const typologySlugs = Array.from(this.dataManager.typologySlugToIdMap.keys());
        typologySlugs.forEach(slug => {
            const element = document.getElementById(slug);
            if (element) {
                element.classList.remove('is--active');
            }
        });
    }
    /**
     * Clear only status filters
     */
    clearStatusFilters() {
        this.activeStatusIds.clear(); // Remove active classes from status filter buttons
        const statusSlugs = Array.from(this.dataManager.statusSlugToIdMap.keys());
        statusSlugs.forEach(slug => {
            const element = document.getElementById(slug);
            if (element) {
                element.classList.remove('is--active');
            }
        });
    }
    /**
     * Deactivate the "all" button for specified filter type
     * @param {string} filterType - 'typology' or 'status'
     * @private
     */
    _deactivateAllButton(filterType = 'typology') {
        const buttonId = filterType === 'typology' 
            ? FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID 
            : FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
        const allButton = document.getElementById(buttonId);
        if (allButton) {
            allButton.classList.remove('is--active');
        }
    }
    /**
     * Check if no filters of this type are active, and if so, activate "all" button
     * @param {string} filterType - 'typology' or 'status'
     * @private
     */
    _checkAndActivateAllButton(filterType = 'typology') {
        const isTypology = filterType === 'typology';
        const hasActiveFilters = isTypology 
            ? this.activeTypologyIds.size > 0
            : this.activeStatusIds.size > 0;
        if (!hasActiveFilters) {
            const buttonId = isTypology 
                ? FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID 
                : FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
            const allButton = document.getElementById(buttonId);
            if (allButton) {
                allButton.classList.add('is--active');
            }
        }
    }
    /**
     * Get current filter state
     * @returns {Object} Current filters
     */
    getCurrentFilters() {
        return {
            typologyIds: Array.from(this.activeTypologyIds),
            statusIds: Array.from(this.activeStatusIds)
        };
    }
    /**
     * Check if any filters are active
     * @returns {boolean}
     */
    hasActiveFilters() {
        return this.activeTypologyIds.size > 0 || 
               this.activeStatusIds.size > 0;
    }
    /**
     * Apply filters with debouncing
     * @private
     */
    _applyFiltersDebounced() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this._applyFiltersNow();
        }, FilterManager.CONFIG.DEBOUNCE_DELAY);
    }
    /**
     * Apply filters immediately
     * @private
     */
    _applyFiltersNow() {
        const filters = this.getCurrentFilters(); // Get filtered data from DataManager
        const filteredLocations = this.dataManager.filterLocations(filters); // Update all visual components
        this.markerManager.updateMarkers(filteredLocations);
        this.clusterHeatManager.update(filteredLocations); // Trigger callbacks
        this._triggerFilterChangeCallbacks(filteredLocations);
    }
    /**
     * Register callback for filter changes
     * @param {Function} callback - Callback function (receives filteredLocations)
     */
    onFilterChange(callback) {
        if (typeof callback === 'function') {
            this.onFilterChangeCallbacks.push(callback);
        }
    }
    /**
     * Trigger all filter change callbacks
     * @private
     */
    _triggerFilterChangeCallbacks(filteredLocations) {
        this.onFilterChangeCallbacks.forEach(callback => {
            try {
                callback(filteredLocations);
            } catch (error) {
                console.error('❌ Error in filter change callback:', error);
            }
        });
    }
    /**
     * Get count of visible items
     * @returns {number}
     */
    getVisibleCount() {
        const filters = this.getCurrentFilters();
        const filteredLocations = this.dataManager.filterLocations(filters);
        return filteredLocations.length;
    }
}
// ! EXPORT
export default FilterManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.FilterManager = FilterManager;
}
